import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Car, Mail, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
      
      const response = await fetch(`${BACKEND_URL}/api/password-reset/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        if (data.email_sent) {
          toast({
            title: "Email Gönderildi",
            description: "Şifre sıfırlama bağlantısı email adresinize gönderildi.",
          });
        } else {
          toast({
            title: "Email Gönderilemedi",
            description: data.email_error || "Email gönderilemedi. Lütfen tekrar deneyin.",
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Hata",
          description: data.detail || "Bir hata oluştu. Lütfen tekrar deneyin.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      toast({
        title: "Hata",
        description: "Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-brand to-brand p-4 rounded-2xl">
              <Car className="h-12 w-12 text-white" />
            </div>
          </div>
          <CardTitle className="text-white text-2xl text-center font-display tracking-tight">Şifremi Unuttum</CardTitle>
          <CardDescription className="text-gray-400 text-center">
            {!isSubmitted 
              ? "Email adresinizi girin, size şifre sıfırlama bağlantısı gönderelim"
              : "Şifre sıfırlama talimatları email adresinize gönderildi"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email Adresi</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className="bg-gray-900 border-gray-700 text-white focus:border-brand"
                  required
                  disabled={isLoading}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-brand to-brand hover:from-brand hover:to-brand/90 text-white"
                disabled={isLoading}
              >
                <Mail className="h-4 w-4 mr-2" />
                {isLoading ? 'Gönderiliyor...' : 'Şifre Sıfırlama Bağlantısı Gönder'}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-brand hover:text-brand text-sm font-semibold">
                  ← Giriş sayfasına dön
                </Link>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <Alert className="bg-green-900/20 border-green-700">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-400 ml-2">
                  Email adresinize şifre sıfırlama bağlantısı gönderildi. Lütfen email kutunuzu (ve spam klasörünü) kontrol edin.
                </AlertDescription>
              </Alert>

              <div className="space-y-2 text-center text-sm text-gray-400">
                <p>Email gelmedi mi?</p>
                <Button
                  variant="outline"
                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                  onClick={() => {
                    setIsSubmitted(false);
                  }}
                >
                  Tekrar dene
                </Button>
              </div>

              <div className="text-center pt-2">
                <Link to="/login" className="text-brand hover:text-brand text-sm font-semibold">
                  ← Giriş sayfasına dön
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
